import { type AdminSupportGrant, CSRF_HEADER, type GovernedProject } from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { NativeSelect } from "@/components/NativeSelect";
import { InlineError, Notice } from "@/components/Notice";
import { PageHeader, SectionHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError, ok } from "../api-errors.ts";
import { formatDateTime } from "../i18n/format.ts";
import { t as translate } from "../i18n/i18n.ts";
import { useSession } from "../session.tsx";
import { useAdminApi } from "./admin-api.tsx";
import { adminRefusal } from "./entity-tree.ts";

const KEY = ["admin", "support-grants"] as const;

/** An operator as tenants see them: never more than a short reference to their account. */
export function operatorLabel(operatorId: string): string {
  const sub = operatorId.slice(operatorId.lastIndexOf("|") + 1);
  return sub.length > 8 ? `${sub.slice(0, 8)}…` : sub;
}

/**
 * Support access (spec `support-access`): ESI's operators ask for time-limited access with a
 * reason; tenant admins approve (for as long or shorter), refuse, revoke, and may open named
 * projects as a reader. Every request an operator makes under a grant is in the audit log.
 */
export function SupportSection() {
  const { t } = useTranslation();
  const { admin } = useAdminApi();
  const grants = useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<AdminSupportGrant[]> => ok(await admin["support-grants"].$get()),
    retry: false,
  });
  const forbidden = grants.error instanceof ApiError && grants.error.status === 403;
  const list = grants.data ?? [];
  const pending = list.filter((g) => g.status === "requested");
  const active = list.filter((g) => g.status === "active");
  const past = list.filter((g) => g.status !== "requested" && g.status !== "active");
  return (
    <div className="flex flex-col gap-8">
      <PageHeader title={t("supportAccess.title")} lead={t("supportAccess.lead")} />
      {forbidden ? (
        <Notice>{t("supportAccess.adminsOnly")}</Notice>
      ) : grants.isError ? (
        <Notice tone="danger">{grants.error.message}</Notice>
      ) : !grants.data ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <>
          <section aria-labelledby="support-pending" className="flex flex-col gap-3">
            <SectionHeader id="support-pending" title={t("supportAccess.pending")} />
            {pending.length === 0 ? (
              <p className="text-sm text-fg-secondary">{t("supportAccess.nonePending")}</p>
            ) : (
              <ul className="flex flex-col gap-3">
                {pending.map((g) => (
                  <PendingGrant key={g.id} grant={g} />
                ))}
              </ul>
            )}
          </section>
          <section aria-labelledby="support-active" className="flex flex-col gap-3">
            <SectionHeader id="support-active" title={t("supportAccess.active")} />
            {active.length === 0 ? (
              <p className="text-sm text-fg-secondary">{t("supportAccess.noneActive")}</p>
            ) : (
              <ul className="flex flex-col gap-3">
                {active.map((g) => (
                  <ActiveGrant key={g.id} grant={g} />
                ))}
              </ul>
            )}
          </section>
          <section aria-labelledby="support-history" className="flex flex-col gap-3">
            <SectionHeader id="support-history" title={t("supportAccess.history")} />
            {past.length === 0 ? (
              <p className="text-sm text-fg-secondary">{t("supportAccess.noHistory")}</p>
            ) : (
              <History grants={past} />
            )}
          </section>
        </>
      )}
    </div>
  );
}

function useDecision(grant: AdminSupportGrant) {
  const { admin } = useAdminApi();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const headers = { headers: { [CSRF_HEADER]: csrfToken } };
  const param = { grantId: grant.id };
  const settle = () => queryClient.invalidateQueries({ queryKey: KEY });
  return {
    approve: useMutation({
      mutationFn: async (hours: number) =>
        ok(
          await admin["support-grants"][":grantId"].approve.$post(
            { param, json: hours < grant.requestedHours ? { hours } : {} },
            headers,
          ),
        ),
      onSettled: settle,
    }),
    refuse: useMutation({
      mutationFn: async () =>
        ok(await admin["support-grants"][":grantId"].refuse.$post({ param }, headers)),
      onSettled: settle,
    }),
    revoke: useMutation({
      mutationFn: async () =>
        ok(await admin["support-grants"][":grantId"].revoke.$post({ param }, headers)),
      onSettled: settle,
    }),
  };
}

function GrantFacts({ grant }: { grant: AdminSupportGrant }) {
  const { t } = useTranslation();
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
      <dt className="text-fg-muted">{t("supportAccess.operator")}</dt>
      <dd className="text-fg-secondary">
        {t("supportAccess.operatorRef", { ref: operatorLabel(grant.operatorId) })}
      </dd>
      <dt className="text-fg-muted">{t("supportAccess.reason")}</dt>
      <dd className="break-words text-fg">{grant.reason}</dd>
      <dt className="text-fg-muted">{t("supportAccess.requested")}</dt>
      <dd className="text-fg-secondary tabular-nums">
        {t("supportAccess.requestedFor", {
          when: formatDateTime(grant.requestedAt),
          count: grant.requestedHours,
        })}
      </dd>
    </dl>
  );
}

function PendingGrant({ grant }: { grant: AdminSupportGrant }) {
  const { t } = useTranslation();
  const id = useId();
  const { approve, refuse } = useDecision(grant);
  const [hours, setHours] = useState(grant.requestedHours);
  const options = [...new Set([1, 2, 4, 8, 24, 48, 72, grant.requestedHours])]
    .filter((h) => h <= grant.requestedHours)
    .sort((a, b) => a - b);
  const error = approve.error ?? refuse.error;
  return (
    <li className="flex flex-col gap-4 rounded-lg border border-line bg-surface-raised p-4">
      <GrantFacts grant={grant} />
      <p className="text-sm text-fg-secondary">{t("supportAccess.metadataOnly")}</p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-hours`} className="text-xs text-fg-muted">
            {t("supportAccess.approveFor")}
          </Label>
          <NativeSelect
            id={`${id}-hours`}
            className="w-40"
            value={String(hours)}
            onChange={(e) => setHours(Number(e.currentTarget.value))}
          >
            {options.map((h) => (
              <option key={h} value={h}>
                {t("supportAccess.hours", { count: h })}
              </option>
            ))}
          </NativeSelect>
        </div>
        <Button
          disabled={approve.isPending || refuse.isPending}
          onClick={() => {
            refuse.reset();
            approve.mutate(hours);
          }}
        >
          {t("supportAccess.approve")}
        </Button>
        <Button
          variant="outline"
          disabled={approve.isPending || refuse.isPending}
          onClick={() => {
            approve.reset();
            refuse.mutate();
          }}
        >
          {t("supportAccess.refuse")}
        </Button>
      </div>
      {error ? <InlineError>{adminRefusal(error)}</InlineError> : null}
    </li>
  );
}

function ActiveGrant({ grant }: { grant: AdminSupportGrant }) {
  const { t } = useTranslation();
  const { revoke } = useDecision(grant);
  const [editing, setEditing] = useState(false);
  return (
    <li className="flex flex-col gap-4 rounded-lg border border-line bg-surface-raised p-4">
      <GrantFacts grant={grant} />
      <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
        <dt className="text-fg-muted">{t("supportAccess.until")}</dt>
        <dd className="text-fg tabular-nums">
          {grant.expiresAt ? formatDateTime(grant.expiresAt) : "—"}
        </dd>
        <dt className="text-fg-muted">{t("supportAccess.content")}</dt>
        <dd className="text-fg-secondary">
          {grant.contentProjects.length > 0
            ? grant.contentProjects.map((p) => p.name).join(", ")
            : t("supportAccess.noContent")}
        </dd>
        <dt className="text-fg-muted">{t("supportAccess.uses")}</dt>
        <dd className="text-fg-secondary tabular-nums">{usesText(null, grant)}</dd>
      </dl>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => setEditing(true)}>
          {t("supportAccess.editContent")}
        </Button>
        <Button variant="destructive" disabled={revoke.isPending} onClick={() => revoke.mutate()}>
          {t("supportAccess.revoke")}
        </Button>
      </div>
      {revoke.error ? <InlineError>{adminRefusal(revoke.error)}</InlineError> : null}
      {editing ? <ContentDialog grant={grant} onClose={() => setEditing(false)} /> : null}
    </li>
  );
}

function usesText(_: null, grant: AdminSupportGrant) {
  return grant.lastUsedAt
    ? translate("supportAccess.usesLast", {
        count: grant.uses,
        when: formatDateTime(grant.lastUsedAt),
      })
    : translate("supportAccess.usesNone");
}

/** Opens named entity projects to the grant, read as a reader (none closes content again). */
function ContentDialog(props: { grant: AdminSupportGrant; onClose: () => void }) {
  const { t } = useTranslation();
  const id = useId();
  const { admin } = useAdminApi();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const projects = useQuery({
    queryKey: ["admin", "projects", "support-content"],
    queryFn: async (): Promise<GovernedProject[]> => ok(await admin.projects.$get({ query: {} })),
    retry: false,
  });
  const [chosen, setChosen] = useState(new Set(props.grant.contentProjectIds));
  const save = useMutation({
    mutationFn: async () =>
      ok(
        await admin["support-grants"][":grantId"].content.$put(
          { param: { grantId: props.grant.id }, json: { projectIds: [...chosen] } },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: KEY });
      props.onClose();
    },
  });
  const entityProjects = (projects.data ?? []).filter(
    (p): p is GovernedProject & { name: string } => p.owner.kind === "entity" && p.name !== null,
  );
  return (
    <AppDialog
      open
      title={t("supportAccess.contentTitle")}
      description={t("supportAccess.contentBody")}
      onClose={props.onClose}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!save.isPending) {
            save.mutate();
          }
        }}
      >
        {projects.isError ? (
          <Notice tone="danger">{projects.error.message}</Notice>
        ) : !projects.data ? (
          <Skeleton className="h-24 w-full" />
        ) : entityProjects.length === 0 ? (
          <p className="text-sm text-fg-secondary">{t("supportAccess.noProjects")}</p>
        ) : (
          <fieldset className="flex max-h-72 flex-col gap-2 overflow-y-auto">
            <legend className="sr-only">{t("supportAccess.contentTitle")}</legend>
            {entityProjects.map((p) => (
              <div key={p.id} className="flex items-center gap-2">
                <Checkbox
                  id={`${id}-${p.id}`}
                  checked={chosen.has(p.id)}
                  onCheckedChange={(checked) => {
                    const next = new Set(chosen);
                    if (checked === true) {
                      next.add(p.id);
                    } else {
                      next.delete(p.id);
                    }
                    setChosen(next);
                  }}
                />
                <Label htmlFor={`${id}-${p.id}`} className="font-normal">
                  {p.name}
                </Label>
              </div>
            ))}
          </fieldset>
        )}
        <p className="text-xs text-fg-muted">{t("supportAccess.contentNote")}</p>
        {save.error ? <Notice tone="danger">{adminRefusal(save.error)}</Notice> : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={props.onClose}>
            {t("app.cancel")}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {t("app.save")}
          </Button>
        </div>
      </form>
    </AppDialog>
  );
}

function History({ grants }: { grants: AdminSupportGrant[] }) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-surface-raised">
      <table className="w-full min-w-[40rem] text-left text-sm">
        <caption className="sr-only">{t("supportAccess.history")}</caption>
        <thead className="border-b border-line text-xs text-fg-muted">
          <tr>
            <th scope="col" className="px-4 py-2 font-medium">
              {t("supportAccess.requested")}
            </th>
            <th scope="col" className="px-4 py-2 font-medium">
              {t("supportAccess.reason")}
            </th>
            <th scope="col" className="px-4 py-2 font-medium">
              {t("supportAccess.statusLabel")}
            </th>
            <th scope="col" className="px-4 py-2 font-medium">
              {t("supportAccess.uses")}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {grants.map((g) => (
            <tr key={g.id} className="align-top">
              <td className="px-4 py-2.5 text-fg-secondary tabular-nums">
                {formatDateTime(g.requestedAt)}
              </td>
              <td className="px-4 py-2.5 break-words text-fg">{g.reason}</td>
              <td className="px-4 py-2.5 text-fg-secondary">
                {t(`supportAccess.status.${g.status}`)}
              </td>
              <td className="px-4 py-2.5 text-fg-secondary tabular-nums">{usesText(null, g)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
